import {resetPassQueuesOnLogout} from "../utils/passSync";
import {clearSuperLikeDraftsOnLogout} from "../utils/superLikeSend";
import {clearLikeErrorSkipsOnLogout} from "../utils/likeFailure";
// Прямой импорт файла (не routing/index): API-клиент не должен тянуть граф роутера.
import {clearPaymentResumeSnapshot} from "../routing/paymentResume";
import {PATHS} from "../routing/paths";
import {markSessionExpired} from "../utils/sessionExpiry";
import {Capacitor} from "@capacitor/core";

// API base: unset/empty = same-origin (/api via nginx in Docker, or Vite proxy in dev — see vite.config.ts).
// Never default to localhost in production builds — that breaks real hosts (wrong origin + CORS).
const rawBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
export const API_BASE_URL =
    rawBase !== undefined && rawBase !== "" ? rawBase : "";

// API Response wrapper type
export interface ApiResponse<T> {
    data: T | null;
    error: string | null;
    /**
     * Машиночитаемый код ошибки бэкенда (`Envelope.err(..., code=...)`, issue #1168):
     * UPLOAD_TOO_LARGE, PHOTO_REQUIRED и т.п. Есть не у всех ошибок — текст в `error`
     * остаётся основным, код нужен, чтобы выбрать поле и понятную формулировку.
     */
    code?: string | null;
    /**
     * Служебные сведения рядом с `data` (если бэкенд их прислал), например причина
     * пустой ленты (`GET /api/feed` → `meta.feedEnd`, issue #1257).
     */
    meta?: Record<string, unknown> | null;
    /**
     * HTTP-статус ответа, если ответ был. Нет статуса — запрос не дошёл или ответ
     * не получен (сеть, таймаут): исход на сервере неизвестен (issue #1260).
     */
    status?: number;
}

/**
 * Query-строка из параметров: пустые (undefined/null/"") пропускаются.
 * Возвращает "?a=1&b=2" либо пустую строку — можно подставлять в шаблон пути.
 */
export function buildQuery(params: Record<string, string | number | boolean | null | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === "") continue;
        search.set(key, String(value));
    }
    const query = search.toString();
    return query ? `?${query}` : "";
}

async function parseApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const raw = await response.text();
    if (!raw) {
        return {
            data: null,
            error: response.ok ? null : `HTTP ${response.status}`,
        };
    }

    try {
        const parsed = JSON.parse(raw) as Partial<ApiResponse<T>> & {
            detail?: string | string[] | { msg?: string }[];
        };
        if ("data" in parsed || "error" in parsed) {
            const result: ApiResponse<T> = {
                data: (parsed.data as T | null) ?? null,
                error: (parsed.error as string | null) ?? null,
            };
            if (typeof parsed.code === "string" && parsed.code) result.code = parsed.code;
            const meta = (parsed as {meta?: unknown}).meta;
            if (meta && typeof meta === "object" && !Array.isArray(meta)) result.meta = meta as Record<string, unknown>;
            return result;
        }
        if (!response.ok && parsed.detail != null) {
            const d = parsed.detail;
            let err: string;
            if (typeof d === "string") err = d;
            else if (Array.isArray(d)) {
                err = d
                    .map((x) => (typeof x === "string" ? x : (x && typeof x === "object" && "msg" in x && x.msg) || JSON.stringify(x)))
                    .join("; ");
            } else err = JSON.stringify(d);
            return {data: null, error: err || `HTTP ${response.status}`};
        }
        return {
            data: response.ok ? (parsed as T) : null,
            error: response.ok ? null : `HTTP ${response.status}`,
        };
    } catch {
        return {
            data: null,
            error: response.ok ? null : `HTTP ${response.status}`,
        };
    }
}

// Token storage
export const tokenStorage = {
    getAccessToken: (): string | null => {
        return localStorage.getItem("accessToken");
    },

    setAccessToken: (token: string): void => {
        localStorage.setItem("accessToken", token);
    },

    getRefreshToken: (): string | null => {
        return localStorage.getItem("refreshToken");
    },

    setRefreshToken: (token: string): void => {
        localStorage.setItem("refreshToken", token);
    },

    clearTokens: (): void => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("isAuthenticated");
        // Пользовательские данные вне аккаунта — не должны доставаться следующему,
        // кто залогинится в этом браузере. Счётчик возвратов здесь НЕ трогаем: он
        // привязан к id пользователя (utils/undoLimit), а удаление при выходе
        // позволяло обнулять дневной лимит простым «выйти и войти».
        clearPaymentResumeSnapshot();
        // Очередь пропусков ленты принадлежит аккаунту: следующему она не достаётся,
        // а ретраи прежнего не уйдут с его токеном.
        resetPassQueuesOnLogout();
        // Черновики сообщений к супер симпатиям — тоже данные аккаунта.
        clearSuperLikeDraftsOnLogout();
        // Анкеты, пропущенные из-за ошибки лайка, скрыты только до конца сессии (#1260).
        clearLikeErrorSkipsOnLogout();
    },

    setTokens: (accessToken: string, refreshToken: string): void => {
        tokenStorage.setAccessToken(accessToken);
        tokenStorage.setRefreshToken(refreshToken);
        localStorage.setItem("isAuthenticated", "true");
    }
};

/** Потолок ожидания обычного запроса. Загрузка медиа идёт мимо apiFetch. */
const DEFAULT_TIMEOUT_MS = 20000;

/**
 * fetch с таймаутом, который срабатывает ДАЖЕ когда AbortController игнорируется.
 *
 * В браузере хватило бы одного AbortController. Но в упакованном Capacitor-приложении
 * включён CapacitorHttp (web/capacitor.config.ts): его патч window.fetch уводит все
 * не-GET запросы в нативный HTTP-стек Android и при этом НЕ пробрасывает туда signal
 * (см. native-bridge.js: в нативный вызов уходят только url/method/data/dataType/headers)
 * и не выставляет connect/read timeout у HttpURLConnection — а его дефолт равен
 * бесконечности. То есть controller.abort() там не отменяет ничего, и повисший POST
 * висел бы вечно: ровно та «чёрная дыра», из-за которой таймаут здесь и появился
 * (useRealtimeUpdates не отрабатывал finally, флаг syncing оставался true, фоновая
 * синхронизация умирала до перезагрузки страницы).
 *
 * Гонка с таймером работает одинаково в браузере и в нативном слое. abort() оставлен,
 * чтобы в браузере запрос реально отменялся, а не просто игнорировался его ответ.
 */
/**
 * В упакованном Android-приложении любой кросс-доменный GET патч Capacitor гонит
 * через прокси-URL `https://localhost/_capacitor_http_interceptor_?u=<реальный url>`,
 * и WebView кэширует ответ по этому URL (заголовков кэширования у API нет, а
 * `cache: "no-store"` на прокси-URL не действует). Итог, воспроизведённый на
 * эмуляторе: GET /api/users/me после PATCH отдавал СТАРЫЙ профиль — человек
 * сохранял фото, видел «изменения применены», но при повторном открытии профиля
 * фото не было (в БД оно лежало). Поэтому в нативной оболочке к GET/HEAD
 * добавляем уникальный параметр: каждый запрос — новый ключ кэша. Бэкенд
 * неизвестные query-параметры игнорирует.
 */
export function withNativeCacheBuster(url: string, method?: string): string {
    const m = (method || "GET").toUpperCase();
    if ((m !== "GET" && m !== "HEAD") || !/^https?:\/\//i.test(url)) return url;
    let native = false;
    try {
        native = Capacitor.isNativePlatform();
    } catch {
        native = false;
    }
    if (!native) return url;
    return `${url}${url.includes("?") ? "&" : "?"}_cb=${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export async function fetchWithTimeout(
    url: string,
    init: RequestInit,
    timeoutMs: number,
): Promise<Response> {
    url = withNativeCacheBuster(url, init.method);
    const controller = new AbortController();
    let timeoutId: number | undefined;
    const onTimeout = new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => {
            controller.abort();
            reject(new DOMException("Превышено время ожидания ответа сервера", "AbortError"));
        }, timeoutMs);
    });
    try {
        return await Promise.race([
            fetch(url, {...init, signal: init.signal ?? controller.signal}),
            onTimeout,
        ]);
    } finally {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    }
}

export type ApiFetchOptions = {
    /** No Authorization header; 401 does not trigger refresh (для публичных presign и т.п.). */
    public?: boolean;
    /** Internal: one retry after token refresh to avoid infinite loops. */
    _retried?: boolean;
    /** Переопределить таймаут (мс) — например, для заведомо долгих операций. */
    timeoutMs?: number;
};

// Fetch wrapper with auth
export async function apiFetch<T>(
    endpoint: string,
    options: RequestInit = {},
    fetchOpts?: ApiFetchOptions
): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Add auth header if token exists
    const accessToken = tokenStorage.getAccessToken();
    const headers: Record<string, string> = {...(options.headers as Record<string, string> | undefined)};
    const hasBody = options.body !== undefined && options.body !== null;
    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
    if (hasBody && !isFormData && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
    }
    if (import.meta.env.DEV) {
        headers["X-Vite-S3-Proxy"] = "1";
    }

    if (!fetchOpts?.public && accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    // У fetch нет таймаута по умолчанию: на «чёрной дыре» (уснувшая мобильная сеть,
    // прокси держит соединение) промис не резолвится никогда. Это ломало не только
    // конкретный запрос — в useRealtimeUpdates не отрабатывал finally, флаг syncing
    // оставался true, и вся фоновая синхронизация умирала до перезагрузки страницы.
    const timeoutMs = fetchOpts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    try {
        const response = await fetchWithTimeout(url, {...options, headers}, timeoutMs);

        const json = await parseApiResponse<T>(response);
        json.status = response.status;

        // Handle 401 - try to refresh token once
        if (
            response.status === 401 &&
            endpoint !== "/api/auth/refresh" &&
            !fetchOpts?.public &&
            !fetchOpts?._retried
        ) {
            const refreshed = await refreshAccessToken();
            if (refreshed === "ok") {
                return apiFetch<T>(endpoint, options, {...fetchOpts, _retried: true});
            }
            if (refreshed === "transient") {
                // Сеть/5xx при refresh — не разлогиниваем, сессия ещё может быть жива.
                return json;
            }
            tokenStorage.clearTokens();
            markSessionExpired();
            if (window.location.pathname !== PATHS.landing) {
                window.location.href = PATHS.landing;
            }
            // status 401: исход известен (не «неоднозначный» сбой без ответа, #1260).
            return {data: null, error: "Сессия истекла. Пожалуйста, войдите снова.", status: 401};
        }
        if (response.status === 401 && fetchOpts?._retried) {
            tokenStorage.clearTokens();
            markSessionExpired();
            if (window.location.pathname !== PATHS.landing) {
                window.location.href = PATHS.landing;
            }
            // status 401: исход известен (не «неоднозначный» сбой без ответа, #1260).
            return {data: null, error: "Сессия истекла. Пожалуйста, войдите снова.", status: 401};
        }

        return json;
    } catch (error) {
        console.error("API Error:", error);
        if (error instanceof DOMException && error.name === "AbortError") {
            return {data: null, error: "Превышено время ожидания ответа сервера"};
        }
        return {
            data: null,
            error: error instanceof Error ? error.message : "Ошибка сети",
        };
    }
}

type RefreshOutcome = "ok" | "invalid" | "transient";

/**
 * Бэкенд ротирует refresh-токен (старый отзывается сразу). Параллельные
 * refresh-запросы (лента+уведомления+чаты ловят 401 одновременно) устраивали
 * гонку: второй запрос уходил с уже отозванным токеном и разлогинивал.
 * Single-flight: все ждут один общий запрос.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

function refreshAccessToken(): Promise<RefreshOutcome> {
    if (!refreshInFlight) {
        refreshInFlight = doRefreshAccessToken().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
}

async function doRefreshAccessToken(): Promise<RefreshOutcome> {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) return "invalid";

    // Свой таймаут: обновление идёт мимо apiFetch, а на его промисе ждут ВСЕ запросы,
    // поймавшие 401 (single-flight). Без ограничения зависший refresh подвешивал
    // приложение целиком — экран так и оставался в состоянии загрузки.
    try {
        const response = await fetchWithTimeout(
            `${API_BASE_URL}/api/auth/refresh`,
            {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({refreshToken}),
            },
            DEFAULT_TIMEOUT_MS,
        );

        const json = await parseApiResponse<{ accessToken: string; refreshToken: string }>(response);

        if (json.data) {
            tokenStorage.setTokens(json.data.accessToken, json.data.refreshToken);
            return "ok";
        }

        // 400/401 — refresh отозван или истёк; прочее (5xx) — временный сбой.
        return response.status === 400 || response.status === 401 ? "invalid" : "transient";
    } catch (error) {
        console.error("Token refresh error:", error);
        return "transient";
    }
}